/*
 * Form comments: threads anchored to a field of a filled form, as the server
 * returns them (`GET …/case_extend_uscis_forms/<id>/comments`), and the pure
 * rules the Comments pane and the page's pins share. Bodies keep the
 * composer's `@Name <user:uid>` tokens.
 */
import dayjs from "dayjs";

export type FormCommentReply = {
  id: string;
  author_uid: string;
  author_name: string | null;
  body: string;
  mentions: string[];
  created_at: string | null;
};

export type FormCommentThread = {
  id: string;
  field_name: string;
  /** The field's page as the form screen numbers it (from 1). */
  page: number | null;
  line_label: string;
  author_uid: string;
  author_name: string | null;
  body: string;
  mentions: string[];
  resolved: boolean;
  resolved_by: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  replies: FormCommentReply[];
  created_at: string | null;
  updated_at: string | null;
  value_at_comment: unknown;
  /** The field holds something else now (an autofill re-run or an edit). */
  value_changed: boolean;
};

export type NewFormComment = {
  field_name: string;
  body: string;
  page?: number | null;
  line_label?: string | null;
};

export type FormCommentFilter = "open" | "resolved";

export const COMMENT_FILTERS: { id: FormCommentFilter; label: string }[] = [
  { id: "open", label: "Open" },
  { id: "resolved", label: "Resolved" }
];

/** Query params a comment link adds to the form's workspace URL. */
export const FORM_COMMENT_PARAMS = {
  field: "field",
  comment: "comment"
} as const;

/** `?field=&comment=` off a search string (a bell row or email link). */
export const readCommentLink = (
  search: string
): { fieldName: string | null; commentId: string | null } => {
  const params = new URLSearchParams(search);
  return {
    fieldName: params.get(FORM_COMMENT_PARAMS.field) || null,
    commentId: params.get(FORM_COMMENT_PARAMS.comment) || null
  };
};

export const inFilter = (t: FormCommentThread, filter: FormCommentFilter) =>
  filter === "resolved" ? t.resolved : !t.resolved;

export const filterCounts = (
  threads: FormCommentThread[]
): Record<FormCommentFilter, number> => ({
  open: threads.filter((t) => !t.resolved).length,
  resolved: threads.filter((t) => t.resolved).length
});

/** The opening comment plus its replies. */
export const commentCount = (t: FormCommentThread): number =>
  1 + t.replies.length;

export type FieldThreadGroup = {
  fieldName: string;
  page: number | null;
  /** The label stored with the oldest thread (used when the field has none on screen). */
  lineLabel: string;
  threads: FormCommentThread[];
};

const byCreated = (a: FormCommentThread, b: FormCommentThread) =>
  (a.created_at ?? "").localeCompare(b.created_at ?? "");

/**
 * The filter's threads by field, fields in page order: `fieldOrder` (the
 * screen's reading order) first, then fields it doesn't list by page and
 * label. Threads on a field oldest first.
 */
export const groupThreadsByField = (
  threads: FormCommentThread[],
  filter: FormCommentFilter,
  fieldOrder: readonly string[] = []
): FieldThreadGroup[] => {
  const rank = new Map(fieldOrder.map((name, i) => [name, i]));
  const groups = new Map<string, FieldThreadGroup>();
  for (const t of threads.filter((x) => inFilter(x, filter)).sort(byCreated)) {
    const group = groups.get(t.field_name);
    if (group) group.threads.push(t);
    else
      groups.set(t.field_name, {
        fieldName: t.field_name,
        page: t.page,
        lineLabel: t.line_label,
        threads: [t]
      });
  }
  const order = (g: FieldThreadGroup) => rank.get(g.fieldName) ?? Infinity;
  return [...groups.values()].sort(
    (a, b) =>
      order(a) - order(b) ||
      (a.page ?? Infinity) - (b.page ?? Infinity) ||
      a.lineLabel.localeCompare(b.lineLabel, undefined, { numeric: true }) ||
      a.fieldName.localeCompare(b.fieldName)
  );
};

export type CommentPinInfo = {
  /** Comments (openings and replies) in the field's open threads, or its resolved ones when none is open. */
  count: number;
  /** Every thread on the field is resolved. */
  resolved: boolean;
  /** The thread a click opens: the oldest open one, else the newest resolved. */
  threadId: string;
  /** Whose initials the pin shows: that thread's author. */
  authorUid: string;
  authorName: string | null;
};

/**
 * One pin per field that has threads, for the page overlay. With `filter`,
 * only that filter's threads count (the page shows open pins, and resolved
 * ones while the Comments pane lists Resolved).
 */
export const commentPinsByField = (
  threads: FormCommentThread[],
  filter?: FormCommentFilter
): Record<string, CommentPinInfo> => {
  const out: Record<string, CommentPinInfo> = {};
  const byField = new Map<string, FormCommentThread[]>();
  const counted = filter ? threads.filter((t) => inFilter(t, filter)) : threads;
  for (const t of [...counted].sort(byCreated)) {
    byField.set(t.field_name, [...(byField.get(t.field_name) ?? []), t]);
  }
  for (const [field, list] of byField) {
    const open = list.filter((t) => !t.resolved);
    const shown = open.length ? open : list;
    const lead = open.length ? open[0] : list[list.length - 1];
    out[field] = {
      count: shown.reduce((n, t) => n + commentCount(t), 0),
      resolved: open.length === 0,
      threadId: lead.id,
      authorUid: lead.author_uid,
      authorName: lead.author_name
    };
  }
  return out;
};

/** Put a thread the server returned into the list (new threads go last). */
export const upsertThread = (
  threads: FormCommentThread[],
  thread: FormCommentThread
): FormCommentThread[] =>
  threads.some((t) => t.id === thread.id)
    ? threads.map((t) => (t.id === thread.id ? thread : t))
    : [...threads, thread];

/** "Now", "12m", "3h", "Yesterday", "Oct 10", "Oct 10, 2025". */
export const commentTime = (
  iso: string | null | undefined,
  now = dayjs()
): string => {
  if (!iso) return "";
  const at = dayjs(iso);
  if (!at.isValid()) return "";
  const minutes = now.diff(at, "minute");
  if (minutes < 1) return "Now";
  if (minutes < 60) return `${minutes}m`;
  if (at.isSame(now, "day")) return `${now.diff(at, "hour")}h`;
  if (at.isSame(now.subtract(1, "day"), "day")) return "Yesterday";
  return at.year() === now.year()
    ? at.format("MMM D")
    : at.format("MMM D, YYYY");
};
